import { useQuery } from "convex/react";
import { useMemo } from "react";

import { api } from "../../../convex/_generated/api";
import { useActiveAccount } from "./activeAccount";

/**
 * Permissions atomiques mirrorées depuis `convex/_lib/permissions.ts`.
 *
 * Source de vérité = backend. Si on ajoute une permission, mettre à jour les
 * deux endroits. Garder cette liste à plat permet à TypeScript d'inférer
 * `Permission` en union de string literals.
 */
export const PERMISSIONS = [
  "team.manage_roles",
  "team.invite",
  "team.manage_compensation",
  "team.mark_payment",
  "team.remove_members",
  // 🩸 Absente de cette liste-ci du 17-06 au 27-08-2026, alors qu'elle vivait
  // cote Convex et etait posee sur CEO/Manager. Rien ne le signalait : aucun
  // panneau ne se gate dessus, et `tsc` ne garde que l'autre sens. Trouvee par
  // la garde de miroir de roleVisibility.test.ts, le jour ou elle a ete ecrite.
  "team.manage_schedules",
  "org.manage_settings",
  "billing.view",
  "billing.manage",
  "creators.view",
  "creators.create",
  "creators.update",
  "creators.archive",
  "sites.view",
  "vault.view",
  "vault.update",
  "vault.delete",
  "persona.view",
  "persona.update",
  "conversations.view",
  "conversations.chat",
  "conversations.schedule",
  "conversations.transactions",
  "spaces.view",
  "spaces.comment",
  "flow.view",
  "flow.edit",
  "flow.publish",
  "fan.view",
  "fan.edit_notes",
  "fan.edit_tags",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Hook : retourne les permissions effectives du caller pour son active account.
 *
 * Pendant le bootstrap (account.listMine pas encore résolu), on retourne
 * `{permissions: null, isLoading: true}` — les consommateurs doivent gérer
 * cet état (typiquement render rien ou un skeleton).
 *
 * Après bootstrap, `permissions` est un `Set<Permission>` — `has(perm)` pour
 * gate du contenu UI.
 *
 * Org active = l'org de SESSION (cf `useActiveAccount`), pas `accounts[0]`. Un
 * user multi-org (ex. chatter ayant aussi fondé sa propre org) résout sinon ses
 * permissions sur la mauvaise org. Mono-org → identique à `accounts[0]`.
 */
export function usePermissions(): {
  permissions: Set<Permission> | null;
  roleSlug: string | null;
  isLoading: boolean;
} {
  const { account: activeAccount, accounts } = useActiveAccount();
  const myPerms = useQuery(
    api.members.getMyPermissionsForAccount,
    activeAccount ? { accountId: activeAccount._id } : "skip",
  );

  return useMemo(() => {
    if (accounts === undefined || (activeAccount && myPerms === undefined)) {
      return { permissions: null, roleSlug: null, isLoading: true };
    }
    if (!activeAccount || !myPerms) {
      return {
        permissions: new Set<Permission>(),
        roleSlug: null,
        isLoading: false,
      };
    }
    return {
      permissions: new Set(myPerms.permissions as Permission[]),
      roleSlug: myPerms.roleSlug,
      isLoading: false,
    };
  }, [accounts, activeAccount, myPerms]);
}

/**
 * Helper : true si le set contient TOUTES les permissions listées (AND).
 * Tolère un set null en retournant false (état chargement → masque par défaut).
 */
export function hasAllPermissions(
  set: Set<Permission> | null,
  required: readonly Permission[],
): boolean {
  if (!set) return false;
  for (const p of required) {
    if (!set.has(p)) return false;
  }
  return true;
}
